import React, { useEffect, useState } from 'react';
import Select, { components } from 'react-select';

const DarkAwareSelect = ({
  isMulti = false,
  isClearable = true,
  ...props
}) => {
  const [isDark, setIsDark] = useState(
    typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const dark = document.documentElement.classList.contains('dark');
      setIsDark(dark);
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => observer.disconnect();
  }, []);

  const themeKey = isDark ? 'dark' : 'light';

  const customStyles = {
    control: (base, state) => ({
      ...base,
      backgroundColor: isDark ? '#18191c' : '#ffffff',
      color: isDark ? '#f3f4f6' : '#111827',
      borderColor: state.isFocused
        ? '#10b981'
        : isDark
        ? '#2a2c32'
        : '#d1d5db',
      borderRadius: '0.5rem',
      padding: '2px 4px',
      boxShadow: state.isFocused
        ? '0 0 0 2px rgba(16, 185, 129, 0.2)'
        : 'none',
      transition: 'all 0.2s ease',
      cursor: 'pointer',
      '&:hover': {
        borderColor: isDark ? '#3f424a' : '#9ca3af',
      },
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: isDark ? '#18191c' : '#ffffff',
      color: isDark ? '#f3f4f6' : '#111827',
      border: isDark ? '1px solid #2a2c32' : '1px solid #e5e7eb',
      zIndex: 50,
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? '#059669'
        : state.isFocused
        ? isDark
          ? '#26272b'
          : '#f3f4f6'
        : 'transparent',
      color: state.isSelected
        ? '#ffffff'
        : isDark
        ? '#f3f4f6'
        : '#111827',
      cursor: 'pointer',
    }),
    singleValue: (base) => ({
      ...base,
      color: isDark ? '#f3f4f6' : '#111827',
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: isDark ? '#26272b' : '#f3f4f6',
      borderRadius: '0.375rem',
      padding: '1px',
      border: isDark ? '1px solid #34373e' : '1px solid #e5e7eb',
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: isDark ? '#f3f4f6' : '#111827',
      fontWeight: 600,
      fontSize: '0.75rem',
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: isDark ? '#9ca3af' : '#4b5563',
      ':hover': {
        backgroundColor: '#ef4444',
        color: '#ffffff',
      },
    }),
    input: (base) => ({
      ...base,
      color: isDark ? '#f3f4f6' : '#111827',
    }),
    placeholder: (base) => ({
      ...base,
      color: isDark ? '#8a8f99' : '#6b7280',
    }),
  };

  const customComponents = {
    ClearIndicator: (clearProps) => (
      <components.ClearIndicator {...clearProps}>
        <span
          style={{
            fontSize: '16px',
            padding: '0 6px',
            cursor: 'pointer',
            color: isDark ? '#cbd5e1' : '#6b7280',
          }}
        >
          ×
        </span>
      </components.ClearIndicator>
    ),
  };

  return (
    <Select
      key={themeKey}
      classNamePrefix="rs"
      isMulti={isMulti}
      isClearable={isClearable}
      components={customComponents}
      styles={customStyles}
      {...props}
    />
  );
};

export default DarkAwareSelect;
